import {
  Box,
  Button,
  DialogTitle,
  Icon,
  IconBackground,
} from '@branch-messenger/willow-ui'
import styled, { css, keyframes } from 'styled-components'

import { largeTabletAndDown } from '@/styles/media'

import { AdvanceCardSelectionProps } from './types'

export const TakeAdvanceButton = styled(Button)`
  ${largeTabletAndDown(css`
    margin: 0 auto;
    width: 100%;
    max-width: 600px;
  `)}
`

export const TakeAdvanceHeaderContainer = styled(Box).attrs({
  $display: 'flex',
  $align: 'center',
  $justify: 'space-between',
  $fullWidth: true,
  $pt: 6,
  $px: 6,
  $pb: 4,
})`
  position: sticky;
  top: 0;
`

export const TakeAdvanceContentBody = styled(Box).attrs({
  $flex: 1,
  $display: 'flex',
  $direction: 'column',
  $align: 'stretch',
  $px: 6,
  $pt: 6,
  $pb: 3,
})`
  overflow-y: auto;
  scrollbar-color: ${({ theme }) => theme.colors.gray} transparent;
  scrollbar-width: thin;
`

export const TakeAdvanceContainer = styled(Box).attrs({
  $display: 'flex',
  $direction: 'column',
})`
  margin: -24px;
  height: 75svh;
  overflow-x: hidden;
  &:has(${TakeAdvanceHeaderContainer}) {
    & ${TakeAdvanceContentBody} {
      padding-top: 0;
    }
  }
`

export const TakeAdvanceContentContainer = styled(Box).attrs({
  $display: 'flex',
  $direction: 'column',
  $fullWidth: true,
  $flex: 1,
  $align: 'stretch',
})`
  overflow: hidden;
  height: 100%;
`

export const StyledTakeAdvanceFooter = styled(Box).attrs<{
  $showShadow?: boolean
}>({
  $display: 'flex',
  $direction: 'column',
  $align: 'initial',
  $gap: 4,
  $px: 6,
  $pb: 6,
  $pt: 3,
  $fullWidth: true,
})`
  z-index: 1;

  ${({ $showShadow }) =>
    $showShadow &&
    css`
      box-shadow: 0 -4px 6px -2px rgba(115, 115, 115, 0.5);
    `}
`

export const StyledTakeAdvanceFooterContainer = styled(Box).attrs({})`
  overflow-x: clip;
  z-index: 1; // ensure footer shadow is above body content
`

export const TakeAdvanceFooterButton = styled(Button).attrs({ size: 'lg' })`
  width: 100%;
`

export const AdvanceStepTitle = styled(DialogTitle)`
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  line-height: 40px;
  letter-spacing: 0.37px;
  vertical-align: middle;
`

export const AdvanceCardSelection = styled(Box).attrs({
  $display: 'flex',
  $align: 'center',
  $gap: 4,
  $py: 6,
  $px: 4,
})<AdvanceCardSelectionProps>`
  border-radius: ${({ theme }) => theme.radii.lg};
  transition:
    background-color 0.2s,
    color 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.infoWeak};
  }

  ${({ $selected = false }) =>
    $selected &&
    css`
      background-color: ${({ theme }) => theme.colors.infoWeak};
    `}
`

export const CardIconContainer = styled(IconBackground).attrs({
  $backgroundColor: 'surface',
})`
  border: 1px solid ${({ theme }) => theme.colors.gray};
`

export const CardSubIconContainer = styled(IconBackground).attrs({
  $backgroundColor: 'selection',
})`
  padding: 2px;
  position: absolute;
  bottom: -10%;
  right: -10%;
  border: 4px solid ${({ theme }) => theme.colors.infoWeak};
`
export const CardSubIcon = styled(Icon).attrs({
  size: 1,
  color: 'textLight',
  fill: 'textLight',
})`
  transform: scale(0.8);
`

const pulseAnimation = keyframes`
  0% {
    box-shadow:  0 0 0 0 rgba(109, 202, 217, 1);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(109, 202, 217, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(109, 202, 217, 0);
  }
`

export const AmountDisclaimer = styled(Box).attrs({
  $display: 'flex',
  $align: 'initial',
  $justify: 'space-around',
  $gap: 4,
  $p: 6,
})`
  border-radius: ${({ theme }) => theme.radii.lg};
  background-color: ${({ theme }) => theme.colors.background};
  border: 2px solid ${({ theme }) => theme.colors.info};
  animation: ${pulseAnimation} 1.5s infinite;
`

export const QuickAmountButton = styled(Button).attrs<{
  $isActive?: boolean
}>({
  size: 'md',
  variant: 'pill',
})`
  flex: 1;
  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      background-color: ${theme.colors.selectionWeak};
      color: ${theme.colors.selection};
    `}
`

export const AmountConfirmationContainer = styled(Box).attrs({
  $display: 'flex',
  $direction: 'column',
  $align: 'initial',
  $gap: 4,
})``
